import { useEffect, useRef } from 'react';
import { useMe } from '@/features/auth/hooks';
import { useTourContext } from './TourProvider';
import { shouldAutoStart, toursFromMe } from './state';
import { isDesktopViewport } from './viewport';
import type { TourId } from './types';

/** Controls for the running tour + `start` for replay entries. */
export function useTour() {
  return useTourContext();
}

/** Fire a tour on the page that hosts it: an explicit replay request (from
 *  anywhere, after navigation) OR a first-visit auto-start. Both wait until
 *  `ready` (the page's data has loaded) so gated targets are mounted before the
 *  present-step set is frozen. Auto-start fires at most once per mount; replay
 *  fires whenever a pending request for this tour is set. */
export function useTourTrigger(tourId: TourId, ready: boolean): void {
  const { active, start, pendingTourId } = useTourContext();
  const { data: me } = useMe();
  const autoAttempted = useRef(false);

  useEffect(() => {
    if (!ready || active || !me) return;
    if (pendingTourId === tourId) {
      autoAttempted.current = true;
      start(tourId);
      return;
    }
    if (autoAttempted.current) return;
    autoAttempted.current = true;
    if (
      shouldAutoStart({
        entry: toursFromMe(me)[tourId],
        isDesktop: isDesktopViewport(),
        termsAccepted: me.terms_required === false,
      })
    ) {
      start(tourId);
    }
  }, [ready, active, me, pendingTourId, tourId, start]);
}
